import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Users = () => {
  const navigate = useNavigate();

  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Mock data - will be replaced with API call
  const mockUsers = [
    {
      user_id: 1,
      username: 'admin',
      full_name: 'System Administrator',
      email: 'admin@cvr.com',
      role: 'admin',
      tenant_name: 'CVR System',
      is_active: true,
      created_at: '2025-01-01T10:00:00',
      last_login: '2025-12-25T14:30:00',
    },
    {
      user_id: 2,
      username: 'auditor1',
      full_name: 'Jane Smith',
      email: 'jane.smith@cvr.com',
      role: 'auditor',
      tenant_name: 'CVR System',
      is_active: true,
      created_at: '2025-01-15T09:00:00',
      last_login: '2025-12-24T16:45:00',
    },
    {
      user_id: 3,
      username: 'john_doe',
      full_name: 'John Doe',
      email: 'john.doe@vendor.com',
      role: 'vendor_user',
      tenant_name: 'Vendor Corp',
      is_active: true,
      created_at: '2025-02-01T11:30:00',
      last_login: '2025-12-25T08:15:00',
    },
    {
      user_id: 4,
      username: 'sarah_vendor',
      full_name: 'Sarah Williams',
      email: 'sarah@vendor2.com',
      role: 'vendor_user',
      tenant_name: 'Vendor Solutions',
      is_active: false,
      created_at: '2025-03-10T14:20:00',
      last_login: '2025-12-20T10:00:00',
    },
  ];

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual API call
      // const response = await userAPI.list();
      // setUsers(response.data);

      // Using mock data for now
      setTimeout(() => {
        setUsers(mockUsers);
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error('Error fetching users:', error);
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const handleRoleFilter = (role) => {
    setFilterRole(role);
  };

  const handleStatusFilter = (status) => {
    setFilterStatus(status);
  };

  const toggleUserSelection = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.user_id));
    }
  };

  const handleAddUser = () => {
    console.log('Add user clicked');
    // TODO: Implement add user functionality
  };

  const handleEditUser = () => {
    console.log('Edit user clicked');
    // TODO: Implement edit user functionality
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      console.log('Deleting user:', userId);
      // TODO: API call to delete user
    }
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Delete ${selectedUsers.length} selected users?`)) {
      console.log('Bulk deleting:', selectedUsers);
      // TODO: API call for bulk delete
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.username.toLowerCase().includes(searchQuery) ||
      user.full_name.toLowerCase().includes(searchQuery) ||
      user.email.toLowerCase().includes(searchQuery);

    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && user.is_active) ||
      (filterStatus === 'inactive' && !user.is_active);

    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return '#8b5cf6';
      case 'auditor': return '#3b82f6';
      case 'vendor_user': return '#10b981';
      default: return '#6b7280';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
    },
    header: {
      backgroundColor: 'white',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      borderBottom: '1px solid #e5e7eb',
    },
    headerContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '20px 30px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    titleSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
    },
    logo: {
      width: '48px',
      height: '48px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '24px',
      fontWeight: 'bold',
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0,
    },
    subtitle: {
      fontSize: '14px',
      color: '#6b7280',
      margin: '2px 0 0 0',
    },
    backButton: {
      padding: '10px 20px',
      backgroundColor: '#6b7280',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    mainContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '30px',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '20px',
      marginBottom: '30px',
    },
    statCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb',
    },
    statLabel: {
      fontSize: '12px',
      fontWeight: '600',
      color: '#6b7280',
      textTransform: 'uppercase',
      marginBottom: '8px',
    },
    statValue: {
      fontSize: '32px',
      fontWeight: 'bold',
    },
    controlsCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb',
      marginBottom: '20px',
    },
    controlsTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    },
    searchBox: {
      flex: 1,
      maxWidth: '400px',
      position: 'relative',
    },
    searchInput: {
      width: '100%',
      padding: '10px 40px 10px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none',
    },
    searchIcon: {
      position: 'absolute',
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#6b7280',
      fontSize: '18px',
    },
    addButton: {
      padding: '10px 20px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    filterRow: {
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
    },
    filterLabel: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
    },
    filterGroup: {
      display: 'flex',
      gap: '8px',
    },
    filterButton: {
      padding: '8px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: '600',
      cursor: 'pointer',
      backgroundColor: 'white',
      transition: 'all 0.2s',
    },
    filterButtonActive: {
      borderColor: '#667eea',
      backgroundColor: '#eff6ff',
      color: '#667eea',
    },
    tableCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
    },
    tableHeader: {
      padding: '16px 24px',
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    tableTitle: {
      fontSize: '16px',
      fontWeight: 'bold',
      color: '#1f2937',
    },
    bulkActions: {
      display: 'flex',
      gap: '8px',
    },
    bulkButton: {
      padding: '6px 12px',
      backgroundColor: '#fee2e2',
      color: '#ef4444',
      border: 'none',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    th: {
      padding: '12px 16px',
      textAlign: 'left',
      fontSize: '12px',
      fontWeight: '600',
      color: '#6b7280',
      textTransform: 'uppercase',
      backgroundColor: '#f9fafb',
      borderBottom: '2px solid #e5e7eb',
    },
    td: {
      padding: '16px',
      borderBottom: '1px solid #e5e7eb',
      fontSize: '14px',
      color: '#374151',
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer',
    },
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    avatar: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      color: 'white',
      fontSize: '16px',
    },
    userName: {
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '2px',
    },
    userEmail: {
      fontSize: '12px',
      color: '#6b7280',
    },
    roleBadge: {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      color: 'white',
      textTransform: 'capitalize',
    },
    statusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
    },
    statusDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
    },
    actionButtons: {
      display: 'flex',
      gap: '8px',
    },
    actionButton: {
      padding: '6px 12px',
      border: 'none',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    editButton: {
      backgroundColor: '#dbeafe',
      color: '#3b82f6',
    },
    deleteButton: {
      backgroundColor: '#fee2e2',
      color: '#ef4444',
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#9ca3af',
    },
    loadingState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#6b7280',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <div style={{ fontSize: '18px' }}>Loading users...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.titleSection}>
            <div style={styles.logo}>👥</div>
            <div>
              <h1 style={styles.title}>User Management</h1>
              <p style={styles.subtitle}>Manage system users and permissions</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            style={styles.backButton}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={styles.mainContent}>
        {/* Stats */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Total Users</div>
            <div style={{ ...styles.statValue, color: '#1f2937' }}>{users.length}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Active Users</div>
            <div style={{ ...styles.statValue, color: '#10b981' }}>
              {users.filter(u => u.is_active).length}
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Administrators</div>
            <div style={{ ...styles.statValue, color: '#8b5cf6' }}>
              {users.filter(u => u.role === 'admin').length}
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Vendors</div>
            <div style={{ ...styles.statValue, color: '#10b981' }}>
              {users.filter(u => u.role === 'vendor_user').length}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div style={styles.controlsCard}>
          <div style={styles.controlsTop}>
            <div style={styles.searchBox}>
              <input
                type="text"
                placeholder="Search users by name, email, or username..."
                value={searchQuery}
                onChange={handleSearch}
                style={styles.searchInput}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              <span style={styles.searchIcon}>🔍</span>
            </div>
            <button
              onClick={handleAddUser}
              style={styles.addButton}
              onMouseEnter={(e) => e.target.style.opacity = '0.9'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              <span>➕</span>
              <span>Add New User</span>
            </button>
          </div>

          <div style={styles.filterRow}>
            <span style={styles.filterLabel}>Filter by Role:</span>
            <div style={styles.filterGroup}>
              {['all', 'admin', 'auditor', 'vendor_user'].map(role => (
                <button
                  key={role}
                  onClick={() => handleRoleFilter(role)}
                  style={{
                    ...styles.filterButton,
                    ...(filterRole === role ? styles.filterButtonActive : {})
                  }}
                >
                  {role === 'all' ? 'All Roles' : role.replace('_', ' ')}
                </button>
              ))}
            </div>

            <span style={styles.filterLabel}>Status:</span>
            <div style={styles.filterGroup}>
              {['all', 'active', 'inactive'].map(status => (
                <button
                  key={status}
                  onClick={() => handleStatusFilter(status)}
                  style={{
                    ...styles.filterButton,
                    ...(filterStatus === status ? styles.filterButtonActive : {})
                  }}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Table */}
        <div style={styles.tableCard}>
          <div style={styles.tableHeader}>
            <div style={styles.tableTitle}>
              {filteredUsers.length} User{filteredUsers.length !== 1 ? 's' : ''}
              {selectedUsers.length > 0 && ` (${selectedUsers.length} selected)`}
            </div>
            {selectedUsers.length > 0 && (
              <div style={styles.bulkActions}>
                <button
                  onClick={handleBulkDelete}
                  style={styles.bulkButton}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#fecaca'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#fee2e2'}
                >
                  🗑️ Delete Selected
                </button>
              </div>
            )}
          </div>

          {filteredUsers.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>👤</div>
              <div style={{ fontSize: '18px', marginBottom: '8px' }}>No users found</div>
              <div style={{ fontSize: '14px' }}>Try adjusting your search or filters</div>
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === filteredUsers.length}
                      onChange={toggleSelectAll}
                      style={styles.checkbox}
                    />
                  </th>
                  <th style={styles.th}>User</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Tenant</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Last Login</th>
                  <th style={styles.th}>Created</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.user_id}>
                    <td style={styles.td}>
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.user_id)}
                        onChange={() => toggleUserSelection(user.user_id)}
                        style={styles.checkbox}
                      />
                    </td>
                    <td style={styles.td}>
                      <div style={styles.userInfo}>
                        <div
                          style={{
                            ...styles.avatar,
                            background: getRoleBadgeColor(user.role)
                          }}
                        >
                          {user.full_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={styles.userName}>{user.full_name}</div>
                          <div style={styles.userEmail}>{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.roleBadge,
                          backgroundColor: getRoleBadgeColor(user.role)
                        }}
                      >
                        {user.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={styles.td}>{user.tenant_name}</td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.statusBadge,
                          backgroundColor: user.is_active ? '#dcfce7' : '#fee2e2',
                          color: user.is_active ? '#16a34a' : '#dc2626',
                        }}
                      >
                        <span
                          style={{
                            ...styles.statusDot,
                            backgroundColor: user.is_active ? '#16a34a' : '#dc2626',
                          }}
                        />
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={styles.td}>{formatDate(user.last_login)}</td>
                    <td style={styles.td}>{formatDate(user.created_at)}</td>
                    <td style={styles.td}>
                      <div style={styles.actionButtons}>
                        <button
                          onClick={() => handleEditUser(user)}
                          style={{ ...styles.actionButton, ...styles.editButton }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#bfdbfe'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#dbeafe'}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.user_id)}
                          style={{ ...styles.actionButton, ...styles.deleteButton }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#fecaca'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#fee2e2'}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;